import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TextAnimate } from "@/components/ui/text-animate";
import WaitlistDialog from "@/components/WaitlistDialog";
import { SiteFooter } from "@/components/SiteFooter";

const BASE_DELAY = 0.45;
const CHAR_SPEED = 0.05;

const Caret = () => (
  <span
    aria-hidden="true"
    className="tw-caret"
    style={{
      display: "inline-block",
      width: "0.08em",
      height: "1em",
      marginInlineStart: "0.04em",
      backgroundColor: "currentColor",
      verticalAlign: "text-bottom",
    }}
  />
);

const makeTypeEffect = (keepEndCaret) => (p, ctx) => {
  const head = Math.min(Math.floor(ctx.globalProgress * ctx.total), ctx.total - 1);
  const typing =
    ctx.globalProgress > 0 && ctx.globalProgress < 1 && ctx.index === head;
  const finished =
    keepEndCaret && ctx.globalProgress >= 1 && ctx.index === ctx.total - 1;
  return {
    style: { opacity: p > 0 ? 1 : 0 },
    content: (
      <>
        {ctx.text}
        {(typing || finished) && !ctx.reduced && <Caret />}
      </>
    ),
  };
};

const SEGMENTS = [
  { text: "Manual", green: true },
  { text: "Chart" },
  { text: "Analysis", green: true },
  { text: "is something" },
  { text: "from the past", mark: true },
  { text: ".", last: true },
];

const UnderlineSvg = () => (
  <svg viewBox="0 0 600 40" preserveAspectRatio="none" aria-hidden="true" focusable="false">
    <defs>
      <mask id="u-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="600" height="40">
        <path
          className="u-draw"
          d="M4 28 C 120 16, 330 10, 594 22"
          stroke="#fff"
          strokeWidth="24"
          fill="none"
          strokeLinecap="round"
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset="1"
        />
      </mask>
    </defs>
    <path
      className="u-fill"
      mask="url(#u-mask)"
      d="M6 28.5 C 100 21, 220 12.5, 350 9 C 450 7, 530 11, 595 19.5 C 598.5 20, 598.5 23, 594 23 C 530 21.5, 450 21, 350 21 C 220 22, 100 27.5, 8 32 C 2.5 32.5, 1.5 29, 6 28.5 Z"
    />
  </svg>
);

function Headline({ onDone }) {
  const segs = useMemo(() => {
    let elapsed = BASE_DELAY;
    return SEGMENTS.map((s) => {
      const duration = s.text.length * CHAR_SPEED;
      const seg = { ...s, delay: elapsed, duration };
      elapsed += duration;
      return seg;
    });
  }, []);

  const totalMs = (segs[segs.length - 1].delay + segs[segs.length - 1].duration) * 1000;

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(onDone, reduced ? 0 : totalMs + 150);
    return () => clearTimeout(t);
  }, [onDone, totalMs]);

  const renderSeg = (seg) => (
    <TextAnimate
      key={seg.text}
      effect={makeTypeEffect(!!seg.last)}
      by="char"
      stagger={1}
      duration={seg.duration}
      delay={seg.delay}
      className={seg.green ? "green" : undefined}
    >
      {seg.text}
    </TextAnimate>
  );

  return (
    <h1 data-testid="hero-headline">
      {renderSeg(segs[0])} {renderSeg(segs[1])} {renderSeg(segs[2])}{" "}
      {renderSeg(segs[3])}{" "}
      <span className="nowrap">
        <span className="mark">
          {renderSeg(segs[4])}
          <UnderlineSvg />
        </span>
        {renderSeg(segs[5])}
      </span>
    </h1>
  );
}

const flowIn = {
  initial: { opacity: 0, y: 18 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

export default function Home() {
  const [typed, setTyped] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [joined, setJoined] = useState(false);

  return (
    <div className={`page${typed ? " typed" : ""}`}>
      <header className="brand">
        <a className="wordmark" href="/" aria-label="Frank369.AI home" data-testid="brand-wordmark">
          Frank369.AI
        </a>
      </header>

      <main>
        <Headline onDone={() => setTyped(true)} />

        <motion.p
          className="sub"
          data-testid="hero-subtitle"
          initial={flowIn.initial}
          animate={typed ? { opacity: 1, y: 0 } : flowIn.initial}
          transition={flowIn.transition}
        >
          Frank369 AI is coming soon.
        </motion.p>

        <motion.button
          className="btn cta"
          type="button"
          aria-haspopup="dialog"
          data-testid="join-waitlist-button"
          initial={flowIn.initial}
          animate={typed ? { opacity: 1, y: 0 } : flowIn.initial}
          transition={{ ...flowIn.transition, delay: 0.18 }}
          onClick={() => setDialogOpen(true)}
        >
          {joined ? "You're on the list" : "Join the waitlist"}
        </motion.button>
      </main>

      <SiteFooter />

      <WaitlistDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onJoined={() => setJoined(true)}
      />
    </div>
  );
}
